import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type ColorPickerSize = "xs" | "sm" | "md" | "lg";

export type ColorPickerProps = Omit<InputHTMLAttributes<HTMLInputElement>, "defaultValue" | "onChange" | "size" | "type" | "value"> & {
  value: string;
  onValueChange: (value: string) => void;
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  disabled?: boolean;
  size?: ColorPickerSize;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  swatchClassName?: string;
};

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

const controlSizeClassName: Record<ColorPickerSize, string> = {
  xs: "bfrs-h-[29px] bfrs-text-[12.5px]",
  sm: "bfrs-h-8 bfrs-text-xs",
  md: "bfrs-h-[34px] bfrs-text-[13px]",
  lg: "bfrs-h-10 bfrs-text-sm"
};

const swatchSizeClassName: Record<ColorPickerSize, string> = {
  xs: "bfrs-size-5",
  sm: "bfrs-size-5",
  md: "bfrs-size-6",
  lg: "bfrs-size-7"
};

const normalizeColorInputValue = (value: string) => (HEX_COLOR_PATTERN.test(value) ? value : "#000000");

export const ColorPicker = forwardRef<HTMLInputElement, ColorPickerProps>(
  (
    {
      className,
      containerClassName,
      labelClassName,
      inputClassName,
      swatchClassName,
      id,
      value,
      onValueChange,
      label,
      helperText,
      errorText,
      disabled,
      size = "md",
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const errorId = errorText ? `${inputId}-error` : undefined;
    const describedBy = [ariaDescribedBy, helperId, errorId].filter(Boolean).join(" ") || undefined;
    const invalid = Boolean(errorText);

    return (
      <div className={cn("bfrs-w-full", containerClassName, className)}>
        {label && (
          <label htmlFor={inputId} className={cn("bfrs-mb-1.5 bfrs-block bfrs-text-[12px] bfrs-font-semibold bfrs-text-text-secondary", disabled && "bfrs-text-text-disabled", labelClassName)}>
            {label}
          </label>
        )}
        <div
          className={cn(
            "bfrs-flex bfrs-w-full bfrs-items-center bfrs-gap-2 bfrs-rounded-[var(--bfrs-input-radius)] bfrs-border bfrs-bg-surface-card bfrs-px-2 bfrs-transition focus-within:bfrs-border-border-focus focus-within:bfrs-ring-2 focus-within:bfrs-ring-primary-soft",
            controlSizeClassName[size],
            invalid ? "bfrs-border-danger focus-within:bfrs-border-danger focus-within:bfrs-ring-danger/15" : "bfrs-border-border",
            disabled && "bfrs-bg-surface-muted bfrs-text-text-disabled"
          )}
        >
          <input
            ref={ref}
            id={inputId}
            type="color"
            value={normalizeColorInputValue(value)}
            disabled={disabled}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? invalid}
            className={cn(
              "bfrs-shrink-0 bfrs-cursor-pointer bfrs-rounded-md bfrs-border bfrs-border-border bfrs-bg-transparent bfrs-p-0 bfrs-outline-none disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-50 [&::-moz-color-swatch]:bfrs-rounded-[5px] [&::-moz-color-swatch]:bfrs-border-0 [&::-webkit-color-swatch-wrapper]:bfrs-p-0 [&::-webkit-color-swatch]:bfrs-rounded-[5px] [&::-webkit-color-swatch]:bfrs-border-0",
              swatchSizeClassName[size],
              swatchClassName
            )}
            onChange={(event) => onValueChange(event.currentTarget.value)}
            {...props}
          />
          <input
            type="text"
            value={value}
            disabled={disabled}
            aria-label={typeof label === "string" ? `${label} hex value` : "Hex color value"}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? invalid}
            className={cn(
              "bfrs-min-w-0 bfrs-flex-1 bfrs-border-0 bfrs-bg-transparent bfrs-font-mono bfrs-uppercase bfrs-text-text-primary bfrs-outline-none placeholder:bfrs-text-text-placeholder disabled:placeholder:bfrs-text-text-muted disabled:bfrs-cursor-not-allowed disabled:bfrs-text-text-disabled",
              inputClassName
            )}
            spellCheck={false}
            onChange={(event) => onValueChange(event.currentTarget.value)}
          />
        </div>
        {errorText ? (
          <p id={errorId} className="bfrs-mt-1.5 bfrs-text-xs bfrs-leading-5 bfrs-text-danger">
            {errorText}
          </p>
        ) : helperText ? (
          <p id={helperId} className="bfrs-mt-1.5 bfrs-text-xs bfrs-leading-5 bfrs-text-text-muted">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);

ColorPicker.displayName = "ColorPicker";
